import type { ReactNode } from 'react';
import {
  ChatInputChip,
  EMessageDirection,
  EMessageType,
  EWidgetType,
  Flex,
  Icon,
  Item,
  ItemContent,
  ItemMedia,
  Markdown,
  type IChatMessage,
} from '@kognitos/lattice';

import i18n from '@/shared/i18n';
import { extractStepReferences } from '@/shared/utils/step-reference';
import { epochMsToDate } from '@/shared/utils/datetime-utils';

import type { IAssistantAccumulator } from '../hooks/types';

export const createRandomMessageId = (): string =>
  `rain-${crypto.randomUUID()}`;

const USER_ACTION_RE = /<user_action\b[^>]*>([\s\S]*?)<\/user_action>/gi;
const HTML_TAG_RE = /<[^>]*>/g;

/**
 * Strip HTML tags to a fixpoint. A single pass over `<[^>]*>` is incomplete
 * sanitization: removing an inner match can splice its neighbours into a fresh
 * tag (e.g. `<<script>script>` -> `<script>`), so overlapping/nested tags can
 * survive one replace. Loop until the string stops shrinking so no reconstructed
 * tag is left behind. (CodeQL: incomplete multi-character sanitization.)
 */
const stripTags = (value: string): string => {
  let prev: string;
  let next = value;
  do {
    prev = next;
    next = next.replace(HTML_TAG_RE, '');
  } while (next !== prev);
  return next;
};

/**
 * User messages sometimes carry a machine sentinel like
 * `<user_action type="make_deterministic">…</user_action>` that carries an
 * instruction to the agent but must never be shown verbatim in the chat. Replace
 * each action with a friendly label (via `labelFor`) or, failing that, its
 * inner text stripped of tags. Content without a user_action is returned
 * unchanged. The raw sentinel is still what gets sent to the agent — this only
 * affects what the user sees.
 */
export const sanitizeUserActionForDisplay = (
  content: string,
  labelFor?: (actionType: string) => string | undefined,
): string => {
  if (!content.includes('<user_action')) return content;
  return content
    .replace(USER_ACTION_RE, (match, inner: string) => {
      const typeMatch = match.match(/\btype=["']([^"']*)["']/i);
      const label = labelFor?.(typeMatch?.[1] ?? '');
      if (label) return label;
      return stripTags(String(inner)).trim();
    })
    .trim();
};

/**
 * Splits a raw outgoing message into its `<step_reference>` tags (if any)
 * and the text that follows, then builds the `content`/`widget` pair for an
 * `IChatMessage` — plain `content` when there's no reference, or a `widget`
 * rendering the same `ChatInputChip` used in the composer's pending row
 * (read-only here) followed by the remaining text in the usual bubble style,
 * when there is. `content` and `widget` are mutually exclusive in
 * `ChatMessage`'s own rendering, so this always returns exactly one of them.
 * Used for both the optimistic send and the server's echoed history so the
 * two reconcile on an identical look.
 */
export const buildOutgoingContent = (
  raw: string,
  toDisplayText: (text: string) => string,
): Pick<IChatMessage, 'content' | 'widget'> => {
  const { references, text } = extractStepReferences(raw);
  if (references.length === 0) {
    return { content: toDisplayText(raw) };
  }

  const displayText = toDisplayText(text);
  return {
    content: '',
    widget: {
      type: EWidgetType.CUSTOM,
      customContent: (
        <Flex direction="column" gap={2.5} align="end" className="w-full">
          <Flex direction="column" gap={2} width="full" align="end">
            {references.map(reference => (
              <ChatInputChip
                key={reference.number}
                chip={{
                  id: String(reference.number),
                  label: i18n.t(
                    'automation.manifestSopView.step.chatReference',
                    {
                      ns: 'automation',
                      number: reference.number,
                      title: reference.title,
                    },
                  ),
                }}
              />
            ))}
          </Flex>
          {displayText && (
            <Flex
              direction="column"
              width="full"
              align="end"
              className="min-w-0 max-w-full rounded-lg bg-muted px-4 py-2.5"
            >
              <Markdown breaks size="sm">
                {displayText}
              </Markdown>
            </Flex>
          )}
        </Flex>
      ),
    },
  };
};

export const upsertById = (
  prev: IChatMessage[],
  next: IChatMessage,
): IChatMessage[] => {
  const idx = prev.findIndex(m => m.id === next.id);
  if (idx === -1) return [...prev, next];
  const copy = prev.slice();
  copy[idx] = next;
  return copy;
};

export const buildCustomMessage = (
  id: string,
  customContent: ReactNode,
): IChatMessage => ({
  id,
  content: '',
  direction: EMessageDirection.INCOMING,
  type: EMessageType.DEFAULT,
  widget: {
    type: EWidgetType.CUSTOM,
    customContent,
  },
});

export const buildStoppedMessage = (id: string): IChatMessage =>
  buildCustomMessage(
    id,
    <Item variant="default" size="sm" className="py-2 pl-0">
      <ItemMedia>
        <Icon type="Info" className="size-3 text-muted-foreground" />
      </ItemMedia>
      <ItemContent>
        <Markdown
          textProps={{ level: 'xSmall', color: 'muted', weight: 'normal' }}
        >
          {i18n.t('rain.message.responseStopped', { ns: 'rain' })}
        </Markdown>
      </ItemContent>
    </Item>,
  );

/**
 * Status line shown when a `save_automation` tool result commits a new draft
 * version, mirroring quill1's "Draft automation updated to v…" message. The
 * version comes from the `save_automation` result (see KOG-11802).
 */
export const buildAutomationSavedMessage = (
  id: string,
  version: string,
): IChatMessage =>
  buildCustomMessage(
    id,
    <Item variant="default" size="sm" className="py-2 pl-0">
      <ItemMedia>
        <Icon type="Info" className="size-3 text-muted-foreground" />
      </ItemMedia>
      <ItemContent>
        <Markdown
          textProps={{ level: 'xSmall', color: 'muted', weight: 'normal' }}
        >
          {i18n.t('automation.quillChat.message.automationBuiltVersion', {
            ns: 'automation',
            version,
          })}
        </Markdown>
      </ItemContent>
    </Item>,
  );

export const buildAssistantMessage = (
  accum: IAssistantAccumulator,
  type: EMessageType,
): IChatMessage => ({
  id: accum.id,
  content: accum.text,
  direction: EMessageDirection.INCOMING,
  type,
  // Only when the stretch carried one. An absent timestamp makes a work block
  // fall back to its no-duration copy, which is right; a fabricated
  // `Date.now()` would report a duration that never happened.
  ...(accum.startedAtMs !== undefined && {
    timestamp: epochMsToDate(accum.startedAtMs),
  }),
});

/** Builds an outgoing (user-side) chat message from free-typed input. */
export const buildUserMessage = (
  id: string,
  content: string,
): IChatMessage => ({
  id,
  content,
  direction: EMessageDirection.OUTGOING,
  type: EMessageType.DEFAULT,
});
